import { useCallback, useEffect, useRef } from 'react'
import { getAuth } from 'firebase/auth'
import { Unsubscribe } from 'firebase/firestore'
import { useDispatch, useSelectorWithRef } from '../../redux/hooks'
import authActions from '../../redux/modules/auth/actions'
import companyActions from '../../redux/modules/company/actions'
import companySelectors from '../../redux/modules/company/selectors'
import companyService from '../../services/companyService'
import { Company } from '@leadgen/models/client'
import { AuthToken } from '../../models/AuthToken'
import { useStateWithRef } from '../shared/useStateWithRef'
import _ from 'lodash'

export const useSubscribeToCompanyChanges = () => {
  const dispatch = useDispatch()
  const unsubscribeRef = useRef<Unsubscribe>()
  const [companyId, setCompanyId, companyIdRef] = useStateWithRef<string | null>(null)
  const [, companyRef] = useSelectorWithRef(companySelectors.getCompany)
  const subscribeToCompany = useCallback(
    async ({ companyId }: { companyId: string }) => {
      const { data, unsubscribe } = await companyService.subscribeToCompany({
        companyId,
        onCompanyUpdated: (company: Company) => {
          dispatch(companyActions.updateCompany(company))
        },
      })
      if (companyIdRef.current !== companyId) {
        unsubscribe()
      } else {
        if (!_.isEqual(companyRef.current, data)) {
          dispatch(companyActions.updateCompany(data))
        }
      }
      unsubscribeRef.current = unsubscribe
      return data
    },
    [companyIdRef, companyRef, dispatch],
  )

  useEffect(() => {
    return getAuth().onAuthStateChanged(async currentUser => {
      if (currentUser) {
        const token = await currentUser.getIdTokenResult(false)
        if (!token) {
          return
        }
        const claims = token.claims as AuthToken
        const companyId = claims.companyId || null
        if (claims.companyId && claims.role) {
          dispatch(authActions.tokenUpdated({ companyId: claims.companyId, role: claims.role }))
        }
        if (companyIdRef.current !== companyId) {
          setCompanyId(companyId || null)
        }
      } else {
        if (companyIdRef.current !== null) {
          setCompanyId(null)
        }
      }
    })
  }, [companyIdRef, dispatch, setCompanyId])

  useEffect(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current()
      unsubscribeRef.current = undefined
    }
    // subscribe to company if login state is rehydrated and the user is logged in (meaning they dont need to call login or register to subscribe to company changes)
    if (companyId) {
      void subscribeToCompany({ companyId })
    }
  }, [companyId, subscribeToCompany])
}
